import React, {useEffect, useState} from 'react'
import {
    Box,
    Button,
    Link,
    Text,
    Flex,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {SkeletonText} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'

/* Components */
import ReviewsHeadline from './partials/reviews-headline'
import ReviewHeading from './partials/reviews-heading'
import ReviewCard from './partials/review-card'
import LoadMore from './partials/load-more'
import ExpandedImage from './partials/expanded-image'
import SubmittedImages from './partials/submitted-images'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

/* API HOOKS */
import {useBazarVoice, useReviewForm, useAllPhotoReviews} from '../../integrations/bazarvoice/hooks/use-bazarvoice'

import {sendLoadReviewEvent} from '../../utils/gtm-utils'
import {useInView} from '../../hooks/use-in-view'
import {useHeaderContext} from '../../contexts/header-context'

const PDPReviews = ({product}) => {
    const {onClose} = useDisclosure()
    const pid = product?.master?.masterId || product?.id;
    const {isLoading, reviews: initialReviews, reviewsError} = useBazarVoice({pid});
    const {allPhotoReviews} = useAllPhotoReviews({pid});
    const {getReviewForm, isLoading: isReviewFormLoading} = useReviewForm({pid})

    const [reviewsData, setReviewsData] = useState(initialReviews);
    const [selectedReview, setSelectedReview] = useState({});
    const [reviewFormData, setReviewFormData] = useState({});
    const [noReviews, setNoReviews] = useState(
        typeof product?.c_bvReviewCount === 'number'
            ? product.c_bvReviewCount === 0
            : null
    );

    const {headerHeight} = useHeaderContext()
    const {ref: bvHeaderRef, isInView} = useInView({
        rootMargin: `${headerHeight}px 0px 400px 0px`
    })

    if (reviewsError) {
        console.error('[PDPReviews]:', reviewsError)
    }

    const fetchReviewForm = async () => {
        try {
            const result = await getReviewForm({pid});

            if (!result.isError) {
                setReviewFormData(result?.data?.Data?.Fields);
            } else {
                console.warn('Review form returned error:', result);
            }
        } catch (error) {
            console.error('Error fetching review form data:', error);
        }
    };

    useEffect(() => {
        if (pid && reviewFormData && Object.keys(reviewFormData).length === 0) {
            fetchReviewForm();
        }
    }, [pid]);

    useEffect(() => {
        if (isInView && !isLoading && initialReviews) {
            // GTM Modification
            sendLoadReviewEvent(product, initialReviews)
            setReviewsData((prev) => {
                // Keep the currently selected sort/order while users scroll the page.
                // Re-initialize only when switching to a different product.
                if (prev?.options?.pid === pid) {
                    return prev
                }

                return {
                    options: {
                        pid: pid,
                        page: 0,
                        sort: 'SubmissionTime:desc'
                    },
                    reviews: initialReviews
                }
            })
            setNoReviews(initialReviews?.Results?.length === 0)
        }
    }, [initialReviews, isInView, isLoading, pid, product])

    const closeAllReviewsModal = () => {
        onClose()
    }

    const renderedReviewCount = reviewsData?.reviews?.Results?.length || 0
    const totalReviews = reviewsData?.reviews?.TotalResults || 0
    const totalReviewsRendered = Math.min(renderedReviewCount, totalReviews)

    return (
        <Box
            maxWidth="926px"
            margin={{base: '0 auto', lg: '0 auto'}}
            ref={bvHeaderRef}>
            <ReviewsHeadline product={product} noReviews={noReviews} isLoading={isReviewFormLoading} reviewFormData={reviewFormData} />

            <Skeleton isLoaded={!isLoading}>
                {/* Submitted images */}
                {isLoading ? (
                    <Flex gap={2} overflowX="auto">
                        {[...Array(5)].map((_, i) => (
                            <Skeleton key={i} minW="80px" h="80px" borderRadius="md" flexShrink={0} />
                        ))}
                    </Flex>
                ) : (
                    !noReviews && (
                        <SubmittedImages
                            reviews={allPhotoReviews}
                            closeAllReviewsModal={closeAllReviewsModal}
                        />
                    )
                )}

                {/* Sort — `ReviewHeading` renders nothing without reviews, so skip the
                    spacing wrapper too instead of leaving an empty margin behind. */}
                {renderedReviewCount > 0 && (
                    <Box m={{base: '36px 0 0', lg: '32px 0 0'}}>
                        <ReviewHeading
                            product={product}
                            reviews={reviewsData}
                            setReviewsData={setReviewsData}
                        />
                    </Box>
                )}

                {/* Review cards */}
                {isLoading ? (
                    [...Array(4)].map((_, i) => (
                        <Box key={i} py={6} borderBottom="1px solid" borderColor="webTheme.strokeTertiary">
                            <Flex gap={3} mb={3} alignItems="center">
                                <Skeleton h="12px" w="80px" />
                                <Skeleton h="12px" w="100px" />
                            </Flex>
                            <Skeleton h="16px" w="200px" mb={3} />
                            <SkeletonText noOfLines={3} spacing={3} />
                        </Box>
                    ))
                ) : (
                    reviewsData?.reviews?.Results?.map((review) => (
                        <ReviewCard
                            key={'rc-desktop-' + review.Id}
                            review={review}
                            setSelectedReview={setSelectedReview}
                            data-testid={`review-card-desktop-${review.Id}`}
                        />
                    ))
                )}

                {/* Number of reviews — the counter is meaningless without rendered
                    reviews, so drop it and its spacing wrapper entirely. */}
                {renderedReviewCount > 0 && (
                    <Box textAlign={{base: 'center'}} mt={{base: 9, lg: 8}}>
                        <Text
                            textStyle='bodyXsRegular'
                            mb={4}
                        >
                            <FormattedMessage
                                defaultMessage="Displaying 1 - {totalReviewsRendered} of {totalReviews}"
                                values={{totalReviewsRendered, totalReviews}}
                                id="pdp_reviews.heading.counter"
                            />
                        </Text>

                        {/* Load More */}
                        <LoadMore
                            pid={pid}
                            product={product}
                            reviews={reviewsData}
                            setReviewsData={setReviewsData}
                        />
                    </Box>
                )}

                {/* Submitted photos modal */}
                <ExpandedImage
                    selectedReview={selectedReview}
                    setSelectedReview={setSelectedReview}
                    closeOtherModals={{closeAllReviewsModal}}
                    allReviews={allPhotoReviews}
                />
            </Skeleton>
        </Box>
    )
}

PDPReviews.propTypes = {
    product: PropTypes.object
}

export default PDPReviews
